import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { buttonVariants } from './ui/button';
import {
  ArchiveIcon,
  AvatarIcon,
  CopyIcon,
  DesktopIcon,
  PersonIcon,
} from '@radix-ui/react-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { AppNav } from './AppNav';
import {
  SCREENS_ROUTE,
  USERS_ROUTE,
  TEMPLATES_ROUTE,
  ACCOUNT_ROUTE,
  DB_ROUTE,
  BETA_SIGNUPS_ROUTE,
} from '@/routes/consts';
import { useAuth } from '@/hooks/useAuth';
import { Role } from '@/generated/graphql';
import { Footer } from './Footer';
import { EnvelopeClosedIcon } from '@radix-ui/react-icons';

type AdminNavItem = {
  id: string;
  title: string;
  href: string;
  icon: React.ReactNode;
  roles: Role[];
};

const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    id: 'screens',
    title: 'Screens',
    href: SCREENS_ROUTE,
    icon: <DesktopIcon />,
    roles: [Role.Admin, Role.User],
  },
  {
    id: 'users',
    title: 'Users',
    href: USERS_ROUTE,
    icon: <PersonIcon />,
    roles: [Role.Admin],
  },
  {
    id: 'templates',
    title: 'Templates',
    href: TEMPLATES_ROUTE,
    icon: <CopyIcon />,
    roles: [Role.Admin],
  },
  {
    id: 'account',
    title: 'Account',
    href: ACCOUNT_ROUTE,
    icon: <AvatarIcon />,
    roles: [Role.Admin, Role.User],
  },
  {
    id: 'db',
    title: 'Database',
    href: DB_ROUTE,
    icon: <ArchiveIcon />,
    roles: [Role.Admin],
  },
  {
    id: 'beta-signups',
    title: 'Beta Signups',
    href: BETA_SIGNUPS_ROUTE,
    icon: <EnvelopeClosedIcon />,
    roles: [Role.Admin],
  },
] as const;

export function AdminContainer({
  section,
  children,
}: {
  children: React.ReactNode;
  section: string;
}) {
  const { user } = useAuth();
  return (
    <div data-name="ADMIN_CONTAINER" className="flex min-h-screen flex-col">
      <AppNav />
      <div
        data-name="ADMIN_CONTAINER_INNER"
        className={`min-w-screen relative z-[2] mt-0 flex flex-1 flex-col justify-center p-2 md:flex-row md:p-0`}
      >
        {user?.role === Role.Admin && (
          <nav
            data-name="ADMIN_SIDE_NAV_CONTAINER"
            className={`flex flex-col border-r`}
          >
            <AdminSideNav currentSection={section} />
          </nav>
        )}

        <main data-name="ADMIN-CONTENT-CONTAINER" className={`flex-1`}>
          <div
            data-name="ADMIN-CONTENT"
            className={`mx-auto max-w-5xl rounded-lg md:p-8`}
          >
            {children}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}

function AdminSideNav({ currentSection }: { currentSection: string }) {
  const { user } = useAuth();
  if (!user || !user.role) return null;
  return (
    <nav
      className={cn(
        `hidden space-x-0 space-y-2 p-6 md:visible md:flex md:flex-col`,
      )}
    >
      {ADMIN_NAV_ITEMS.filter((item) => item.roles.includes(user?.role)).map(
        (item) => (
          <Link
            key={item.href}
            to={item.href}
            className={cn(
              buttonVariants({ variant: 'ghost' }),
              currentSection === item.id
                ? `bg-accent text-inherit hover:bg-accent`
                : `text-muted-foreground hover:bg-transparent`,
              'justify-start',
            )}
          >
            <div className="flex flex-row items-center space-x-2">
              <Tooltip>
                <TooltipTrigger asChild>{item.icon}</TooltipTrigger>
                <TooltipContent>
                  <p>{item.title}</p>
                </TooltipContent>
              </Tooltip>
              {/* only show title on desktop */}
              <div className={`hidden lg:block`}>{item.title}</div>
            </div>
          </Link>
        ),
      )}
    </nav>
  );
}
